/*
 * <cta-rail>: the buy shortcut at the bottom of the screen.
 *
 * Hidden by default (inert, aria-hidden, translated out). Shown whenever the page's
 * own buy button is off screen, above or below: the hero button ([data-hero-cta]),
 * or, without one, the buy buttons (data-watch-id). Hidden while that button or any
 * [data-rail-hide] block is on screen, and while the scroll sequence is pinned
 * (html.is-seq-pinned). In link mode (the landing page) there is no buy box. While shown it sets
 * --rail-h on <html>, which feeds scroll-padding-bottom, so focused elements are
 * never scrolled underneath it. It follows `variant:change` for the color dot,
 * name, price and sold-out state. Motion is CSS only and off for reduced motion.
 */
(() => {
  'use strict';

  const root = document.documentElement;

  class CtaRail extends HTMLElement {
    connectedCallback() {
      const buyId = this.dataset.watchId || this.dataset.buyId;
      this.buyBox = buyId ? document.getElementById(buyId) : null;
      this.hideZones = [...document.querySelectorAll('[data-rail-hide]')];
      this.visibleZones = new Set();
      this.seam = this.querySelector('.seam');
      this.shown = false;
      this.ctaOff = false;
      this.buyVisible = false;

      this.update = this.update.bind(this);
      this.onVariant = this.onVariant.bind(this);
      this.onIntersect = this.onIntersect.bind(this);

      root.classList.add('has-cta-rail');
      document.addEventListener('variant:change', this.onVariant);
      this.syncVariant();

      if (!('IntersectionObserver' in window)) return;

      // The first hero button that is laid out (a hidden duplicate would count as "scrolled past").
      const heroButtons = [...document.querySelectorAll('[data-hero-cta]')];
      this.heroCta = heroButtons.find((el) => el.getClientRects().length > 0) || heroButtons[0] || null;
      // Without a hero button or buy buttons there is nothing to measure, so the rail stays hidden.
      if (!this.heroCta && !this.buyBox) return;

      // Anything under the sticky header is already out of sight.
      const header = document.querySelector('.header--sticky');
      const top = header ? Math.round(header.getBoundingClientRect().height) : 0;
      this.headerTop = top;
      this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: `-${top}px 0px 0px 0px` });
      if (this.heroCta) this.observer.observe(this.heroCta);
      if (this.buyBox) this.observer.observe(this.buyBox);
      this.hideZones.forEach((zone) => this.observer.observe(zone));

      this.classWatcher = new MutationObserver(this.update);
      this.classWatcher.observe(root, { attributes: true, attributeFilter: ['class'] });

      // An instant jump over the hero button (a #link, a script scroll) never crosses an
      // intersection threshold, so the scroll position is checked too, once per frame.
      if (this.heroCta) addEventListener('scroll', this.onScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizer = new ResizeObserver(() => {
          if (this.shown) this.setRailHeight();
        });
        this.resizer.observe(this);
      }
    }

    onScroll = () => {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = 0;
        const rect = this.heroCta.getBoundingClientRect();
        const off = rect.bottom <= this.headerTop || rect.top >= window.innerHeight;
        if (off === this.ctaOff) return;
        this.ctaOff = off;
        this.update();
      });
    };

    disconnectedCallback() {
      removeEventListener('scroll', this.onScroll);
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = 0;
      document.removeEventListener('variant:change', this.onVariant);
      if (this.observer) this.observer.disconnect();
      if (this.classWatcher) this.classWatcher.disconnect();
      if (this.resizer) this.resizer.disconnect();
      root.classList.remove('has-cta-rail');
      root.style.removeProperty('--rail-h');
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.target === this.buyBox) {
          this.buyVisible = entry.isIntersecting;
          if (!this.heroCta) this.ctaOff = !entry.isIntersecting;
        } else if (entry.target === this.heroCta) {
          this.ctaOff = !entry.isIntersecting;
        } else if (entry.isIntersecting) {
          this.visibleZones.add(entry.target);
        } else {
          this.visibleZones.delete(entry.target);
        }
      });
      this.update();
    }

    update() {
      const pinned = root.classList.contains('is-seq-pinned');
      const show = this.ctaOff && !this.buyVisible && this.visibleZones.size === 0 && !pinned;
      if (show === this.shown) return;
      this.shown = show;

      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
      this.setAttribute('aria-hidden', String(!show));

      if (show) {
        this.setRailHeight();
        // The seam zips closed the first time the rail arrives (resting state is closed).
        if (this.seam) this.seam.classList.add('is-visible');
      } else {
        root.style.removeProperty('--rail-h');
      }
    }

    // Space the rail covers at the bottom of the viewport. Layout values only,
    // so the slide-in transform does not skew the measurement.
    setRailHeight() {
      const bottom = parseFloat(window.getComputedStyle(this).bottom) || 0;
      root.style.setProperty('--rail-h', `${Math.ceil(this.offsetHeight + bottom)}px`);
    }

    // Start from the variant the primary form currently holds.
    syncVariant() {
      const form = document.getElementById(this.dataset.formId);
      const script = form && form.querySelector('script[data-variants]');
      if (!script) return;
      let variants;
      try {
        variants = JSON.parse(script.textContent);
      } catch (error) {
        return;
      }
      if (!Array.isArray(variants)) return;
      const field = form.querySelector('input[name="id"]:checked, select[name="id"]');
      const id = field ? String(field.value) : '';
      const variant = variants.find((item) => String(item.id) === id);
      if (variant) this.render(variant);
    }

    onVariant(event) {
      const detail = event.detail || {};
      if (!detail.variant || detail.formId !== this.dataset.formId) return;
      this.render(detail.variant);
    }

    render(variant) {
      const options = Array.isArray(variant.options) ? variant.options : [];
      const name = this.querySelector('[data-rail-name]');
      if (name) name.textContent = options.length === 1 ? options[0] : variant.title;

      const dot = this.querySelector('[data-rail-dot]');
      if (dot) {
        dot.hidden = !variant.swatch;
        if (variant.swatch) dot.style.setProperty('--swatch', variant.swatch);
      }

      // No price part (and no separator) until the price is above zero.
      const hasPrice = Number(variant.price_amount) > 0;
      this.querySelectorAll('[data-rail-price-group]').forEach((el) => {
        el.hidden = !hasPrice;
      });
      this.querySelectorAll('[data-rail-price]').forEach((el) => {
        el.textContent = variant.price;
      });

      const button = this.querySelector('[data-rail-add]');
      if (button) {
        button.disabled = !variant.available;
        const label = button.querySelector('[data-rail-add-label]');
        const text = variant.available ? button.dataset.labelAdd : button.dataset.labelSoldOut;
        if (label && text) label.textContent = text;
      }
    }
  }

  if (!customElements.get('cta-rail')) customElements.define('cta-rail', CtaRail);
})();
